import { Head, useForm, usePage } from "@inertiajs/react"
import {
  GraduationCap,
  Upload,
  FileText,
  ShieldCheck,
  Star,
  X,
} from "lucide-react"
import { useRef } from "react"

import InputError from "@/components/input-error"
// TODO: Replace with global camelcase-keys transform in inertia.tsx (see docs/todos/refactor-global-case-conversion.md)
import { useFlashcardProps } from "@/hooks/use-flashcard-props"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Spinner } from "@/components/ui/spinner"
import MarketingLayout from "@/layouts/marketing-layout"
import type { TeacherOnboardingProps } from "@/types/teacher"

export default function TeacherOnboarding(props: Record<string, unknown>) {
  const { teacherProfile, availableCredentials } =
    useFlashcardProps<TeacherOnboardingProps>(props)
  const { errors } = usePage().props as unknown as {
    errors: Record<string, string>
  }
  const certInputRef = useRef<HTMLInputElement>(null)
  const docInputRef = useRef<HTMLInputElement>(null)

  const credentials = availableCredentials ?? []

  const { data, setData, put, processing } = useForm({
    teacher_profile: {
      bio: teacherProfile.bio || "",
      teaching_experience: teacherProfile.teachingExperience || "",
      english_proficiency: teacherProfile.englishProficiency || "",
      professional_background: teacherProfile.professionalBackground || "",
      certifications: [] as File[],
      verification_documents: [] as File[],
    },
    credential_ids: (teacherProfile.credentialIds ?? []) as number[],
  })

  const profile = data.teacher_profile

  function setProfileField<K extends keyof typeof profile>(
    field: K,
    value: (typeof profile)[K],
  ) {
    setData("teacher_profile", { ...profile, [field]: value })
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    put("/teacher/onboarding", {
      forceFormData: true,
    })
  }

  function toggleCredential(id: number, checked: boolean) {
    setData(
      "credential_ids",
      checked
        ? [...data.credential_ids, id]
        : data.credential_ids.filter((cid) => cid !== id),
    )
  }

  function handleFileAdd(
    field: "certifications" | "verification_documents",
    files: FileList | null,
  ) {
    if (!files) return
    const newFiles = Array.from(files)
    setProfileField(field, [...profile[field], ...newFiles])
  }

  function handleFileRemove(
    field: "certifications" | "verification_documents",
    index: number,
  ) {
    setProfileField(
      field,
      profile[field].filter((_, i) => i !== index),
    )
  }

  return (
    <MarketingLayout>
      <Head title="Teacher Onboarding" />

      <div className="min-h-screen bg-gradient-to-b from-indigo-50/50 to-white dark:from-slate-900 dark:to-slate-950">
        <div className="container mx-auto max-w-2xl px-4 py-12 sm:px-6 sm:py-16">
          {/* Header */}
          <div className="mb-10 text-center">
            <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-lg shadow-indigo-500/25">
              <GraduationCap className="size-7 text-white" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
              Complete Your Teacher Profile
            </h1>
            <p className="mt-2 text-base text-slate-600 dark:text-slate-400">
              Help learners understand your qualifications. This information will
              be reviewed before your profile goes live.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Bio */}
            <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">
                About You
              </h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="bio">Bio</Label>
                  <p className="mb-2 text-sm text-slate-500 dark:text-slate-400">
                    Introduce yourself to potential students. What makes you a
                    great Thai teacher?
                  </p>
                  <Textarea
                    id="bio"
                    value={profile.bio}
                    onChange={(e) => setProfileField("bio", e.target.value)}
                    placeholder="I've been teaching Thai for 5 years, specializing in conversational Thai for expats..."
                    className="min-h-28"
                  />
                  <InputError message={errors.bio} className="mt-1" />
                </div>
              </div>
            </section>

            {/* Experience & Qualifications */}
            <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">
                Experience & Qualifications
              </h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="teaching_experience">
                    Teaching Experience
                  </Label>
                  <p className="mb-2 text-sm text-slate-500 dark:text-slate-400">
                    Years of experience, types of students, teaching context
                  </p>
                  <Textarea
                    id="teaching_experience"
                    value={profile.teaching_experience}
                    onChange={(e) =>
                      setProfileField("teaching_experience", e.target.value)
                    }
                    placeholder="5 years teaching Thai to foreign students, both group and private lessons..."
                    className="min-h-24"
                  />
                  <InputError
                    message={errors.teaching_experience}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="english_proficiency">
                    English Proficiency{" "}
                    <span className="font-normal text-slate-400">
                      (optional)
                    </span>
                  </Label>
                  <p className="mb-2 text-sm text-slate-500 dark:text-slate-400">
                    IELTS, TOEFL score, or equivalent certification
                  </p>
                  <Input
                    id="english_proficiency"
                    value={profile.english_proficiency}
                    onChange={(e) =>
                      setProfileField("english_proficiency", e.target.value)
                    }
                    placeholder="IELTS 7.5, or equivalent"
                  />
                  <InputError
                    message={errors.english_proficiency}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="professional_background">
                    Professional Background
                  </Label>
                  <p className="mb-2 text-sm text-slate-500 dark:text-slate-400">
                    Relevant professional experience (e.g., MNC experience where
                    English was primary language)
                  </p>
                  <Textarea
                    id="professional_background"
                    value={profile.professional_background}
                    onChange={(e) =>
                      setProfileField(
                        "professional_background",
                        e.target.value,
                      )
                    }
                    placeholder="Worked at a multinational company for 3 years where English was the primary language..."
                    className="min-h-24"
                  />
                  <InputError
                    message={errors.professional_background}
                    className="mt-1"
                  />
                </div>
              </div>
            </section>

            {/* Highlights */}
            {credentials.length > 0 && (
              <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-slate-900 dark:text-white">
                  <Star className="size-[18px] text-indigo-500" />
                  Highlights
                </h2>
                <p className="mb-5 text-sm text-slate-500 dark:text-slate-400">
                  Select credentials that apply to you. These appear as badges
                  on your public profile.
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {credentials.map((credential) => {
                    const isChecked = data.credential_ids.includes(
                      credential.id,
                    )
                    return (
                      <label
                        key={credential.id}
                        className={`flex cursor-pointer items-center gap-3 rounded-lg border-[1.5px] px-3.5 py-3 transition-all select-none ${isChecked
                          ? "border-emerald-500 bg-emerald-50 dark:border-emerald-600 dark:bg-emerald-950/30"
                          : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50 dark:hover:border-slate-600"
                          }`}
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={(checked) =>
                            toggleCredential(credential.id, checked === true)
                          }
                          className={
                            isChecked
                              ? "border-emerald-500 bg-emerald-500 text-white data-[state=checked]:border-emerald-500 data-[state=checked]:bg-emerald-500"
                              : ""
                          }
                        />
                        <span
                          className={`text-sm font-medium ${isChecked
                            ? "text-emerald-700 dark:text-emerald-400"
                            : "text-slate-700 dark:text-slate-300"
                            }`}
                        >
                          {credential.name}
                        </span>
                      </label>
                    )
                  })}
                </div>

                {/* Supporting Documents */}
                <div className="mt-6 border-t border-slate-100 pt-5 dark:border-slate-800">
                  <Label className="mb-2 flex items-center gap-2">
                    <FileText className="size-4 text-indigo-500" />
                    Supporting Documents
                  </Label>
                  <p className="mb-3 text-sm text-slate-500 dark:text-slate-400">
                    Upload certificates or proof for your selected highlights
                    (e.g., TESOL cert, degree, language test scores). Profiles
                    with supporting documents get verified faster.
                  </p>
                  <input
                    ref={certInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    multiple
                    className="hidden"
                    onChange={(e) =>
                      handleFileAdd("certifications", e.target.files)
                    }
                  />
                  <button
                    type="button"
                    onClick={() => certInputRef.current?.click()}
                    className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500 transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-600 dark:border-slate-700 dark:bg-slate-800/50 dark:hover:border-indigo-700 dark:hover:bg-indigo-950/30 dark:hover:text-indigo-400"
                  >
                    <Upload className="size-5" />
                    Upload supporting documents (PDF, JPG, PNG)
                  </button>
                  {/* Existing Certifications */}
                  {teacherProfile.existingCertifications &&
                    teacherProfile.existingCertifications.length > 0 && (
                      <ul className="mt-3 space-y-2">
                        {teacherProfile.existingCertifications.map((file) => (
                          <li
                            key={file.id}
                            className="flex items-center justify-between rounded-lg bg-emerald-50 px-3 py-2 text-sm dark:bg-emerald-950/30"
                          >
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noreferrer"
                              className="truncate font-medium text-emerald-700 hover:underline dark:text-emerald-400"
                            >
                              {file.filename}
                            </a>
                            <span className="text-xs text-slate-400">
                              Uploaded
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  {/* New Uploads */}
                  {profile.certifications.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {profile.certifications.map((file, i) => (
                        <li
                          key={i}
                          className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm dark:bg-slate-800/50"
                        >
                          <span className="truncate text-slate-700 dark:text-slate-300">
                            {file.name}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              handleFileRemove("certifications", i)
                            }
                            className="ml-2 shrink-0 text-slate-400 hover:text-red-500"
                          >
                            <X className="size-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <InputError
                    message={errors.certifications}
                    className="mt-1"
                  />
                </div>
              </section>
            )}

            {/* Document Uploads */}
            <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">
                Verification Documents
              </h2>
              <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
                Upload documents that establish your credibility. These are
                reviewed by our team and not shown publicly.
              </p>

              {/* Verification Documents */}
              <div>
                <Label className="mb-2 flex items-center gap-2">
                  <ShieldCheck className="size-4 text-indigo-500" />
                  Identity / Qualification Proof
                </Label>
                <p className="mb-3 text-sm text-slate-500 dark:text-slate-400">
                  Government ID, degree, or other proof of qualifications
                </p>
                <input
                  ref={docInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  className="hidden"
                  onChange={(e) =>
                    handleFileAdd("verification_documents", e.target.files)
                  }
                />
                <button
                  type="button"
                  onClick={() => docInputRef.current?.click()}
                  className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500 transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-600 dark:border-slate-700 dark:bg-slate-800/50 dark:hover:border-indigo-700 dark:hover:bg-indigo-950/30 dark:hover:text-indigo-400"
                >
                  <Upload className="size-5" />
                  Click to upload documents (PDF, JPG, PNG)
                </button>
                {/* Existing Verification Documents */}
                {teacherProfile.existingVerificationDocuments &&
                  teacherProfile.existingVerificationDocuments.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {teacherProfile.existingVerificationDocuments.map(
                        (file) => (
                          <li
                            key={file.id}
                            className="flex items-center justify-between rounded-lg bg-emerald-50 px-3 py-2 text-sm dark:bg-emerald-950/30"
                          >
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noreferrer"
                              className="truncate font-medium text-emerald-700 hover:underline dark:text-emerald-400"
                            >
                              {file.filename}
                            </a>
                            <span className="text-xs text-slate-400">
                              Uploaded
                            </span>
                          </li>
                        ),
                      )}
                    </ul>
                  )}
                {/* New Uploads */}
                {profile.verification_documents.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {profile.verification_documents.map((file, i) => (
                      <li
                        key={i}
                        className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm dark:bg-slate-800/50"
                      >
                        <span className="truncate text-slate-700 dark:text-slate-300">
                          {file.name}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            handleFileRemove("verification_documents", i)
                          }
                          className="ml-2 shrink-0 text-slate-400 hover:text-red-500"
                        >
                          <X className="size-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <InputError
                  message={errors.verification_documents}
                  className="mt-1"
                />
              </div>
            </section>

            {/* Submit */}
            <div className="flex flex-col items-center gap-3">
              <Button
                type="submit"
                size="lg"
                disabled={processing}
                className="w-full gap-2 shadow-lg shadow-indigo-500/20 sm:w-auto sm:px-12"
              >
                {processing && <Spinner />}
                Submit for Review
              </Button>
              <p className="text-center text-sm text-slate-400 dark:text-slate-500">
                Your profile will be reviewed within 48-72 business hours.
              </p>
            </div>
          </form>
        </div>
      </div>
    </MarketingLayout>
  )
}
